import { clamp } from 'lodash-es'
import { Dimensions } from './types'

export function toRad(deg: number) {
  return deg * (Math.PI / 180)
}

export function toDeg(rad: number) {
  return rad * (180 / Math.PI)
}

// Checks if value is between start and end, extremes included.
export function isBetween(range: [number, number], value: number) {
  // sort range to have [min, max]
  range.sort((a, b) => a - b)
  const [min, max] = range
  return value >= min && value <= max
}

export function mapValues(n: number, start1: number, stop1: number, start2: number, stop2: number) {
  return ((n - start1) / (stop1 - start1)) * (stop2 - start2) + start2
}

export function mapClampValues(
  n: number,
  start1: number,
  stop1: number,
  start2: number,
  stop2: number
) {
  const mapped = mapValues(n, start1, stop1, start2, stop2)
  if (start2 < stop2) {
    return clamp(mapped, start2, stop2)
  } else {
    return clamp(mapped, stop2, start2)
  }
}

export function percentageChange(oldNumber: number, newNumber: number) {
  const decreaseValue = oldNumber - newNumber
  return (decreaseValue / newNumber) * 100
}

/**
 * Compute value of x modulo y. This function returns
 * positive values for negative x, while `%` operator
 * doesn't:
 *
 * ```
 * mod(10,3) // 1
 * mod(-10,3) // 2
 * ```
 *
 * instead of
 * ```
 * 10 % 3  // 1
 * -10 % 3 // -1
 * ```
 *
 */
export function mod(x: number, y: number) {
  return x - y * Math.floor(x / y)
}

export function easeSin([edge0, edge1]: [number, number], x: number): number {
  const cx = clamp((x - edge0) / (edge1 - edge0), 0, 1)
  return Math.sin((cx * Math.PI) / 2)
}

export function easeBell([step0, step1]: [number, number], x: number) {
  const cx = clamp(x, 0, 1)
  return easeSin([0, step0], cx) * easeSin([1, step1], cx)
}

/**
 * Scale down input width and height to reach an aspect ratio
 * lower than or equal to `maxRatio`.
 */
export function maxAspectRatio({ width, height }: Dimensions, maxRatio: number): Dimensions {
  if (height > width) {
    return { width, height: Math.min(width * maxRatio, height) }
  } else {
    return { width: Math.min(height * maxRatio, width), height }
  }
}
